import { useState } from 'react';
import {
  FlaskConical,
  ListChecks,
  FileText,
  ArrowRight,
  FolderOutput,
  History,
  Trash2,
  Eye,
  LayoutGrid,
  ScanSearch,
  Microscope,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SourceSelector } from '@/features/evaluation/components/SourceSelector';
import { EvalRunner } from '@/features/evaluation/components/EvalRunner';
import { KanbanBoard } from '@/features/evaluation/components/KanbanBoard';
import { useCriteria } from '@/features/criteria/hooks/useCriteria';
import {
  useEvaluations,
  useDeleteEvaluation,
  useBucketEvaluations,
} from '@/features/evaluation/hooks/useEvaluation';
import { cn } from '@/lib/utils';
import { CRITERION_TYPE_LABELS } from '@/features/criteria/types';
import {
  BUCKET_LABELS,
  getBucketCategory,
} from '@/features/evaluation/types';
import type { Criterion } from '@/features/criteria/types';
import type { BatchEvaluationProgress, Evaluation, EvaluationMode } from '@/features/evaluation/types';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export function Evaluate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedSource = searchParams.get('source');
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>(
    preselectedSource ? [preselectedSource] : [],
  );
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('setup');
  const [evaluationMode, setEvaluationMode] = useState<EvaluationMode>('screening');

  const { data: criteria = [], isLoading: criteriaLoading } = useCriteria();
  const { data: evaluations = [], isLoading: evalsLoading } = useEvaluations();
  const deleteEvaluation = useDeleteEvaluation();
  const bucketEvaluations = useBucketEvaluations();

  function toggleCriterion(id: string) {
    setSelectedCriteriaIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  }

  function selectAllCriteria() {
    if (selectedCriteriaIds.length === criteria.length) {
      setSelectedCriteriaIds([]);
    } else {
      setSelectedCriteriaIds(criteria.map((c) => c.id));
    }
  }

  function handleEvalComplete(_progress: BatchEvaluationProgress) {
    setActiveTab('history');
  }

  async function handleAutoBucket() {
    const completedEvals = evaluations.filter(
      (e) => e.status === 'completed' && e.score !== null
    );
    if (completedEvals.length === 0) {
      toast.error('No completed evaluations to bucket');
      return;
    }
    await bucketEvaluations.mutateAsync({ evaluations: completedEvals });
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Evaluate</h1>
          <p className="text-sm text-slate-500">
            Score sources against your criteria using AI
          </p>
        </div>
        {evaluations.some((e) => e.status === 'completed') && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoBucket}
            disabled={bucketEvaluations.isPending}
          >
            <FolderOutput className="mr-2 h-4 w-4" />
            Auto-Bucket Results
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="setup">
            <FlaskConical className="mr-1.5 h-4 w-4" />
            New Evaluation
          </TabsTrigger>
          <TabsTrigger value="kanban">
            <LayoutGrid className="mr-1.5 h-4 w-4" />
            Kanban
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-1.5 h-4 w-4" />
            History
            {evaluations.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {evaluations.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Setup Tab */}
        <TabsContent value="setup" className="mt-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left: Source Selector */}
            <div className="lg:col-span-1">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-600" />
                  <h2 className="text-sm font-semibold text-slate-900">
                    1. Select Sources
                  </h2>
                </div>
                <SourceSelector
                  selectedIds={selectedSourceIds}
                  onSelectionChange={setSelectedSourceIds}
                />
              </div>
            </div>

            {/* Center: Criteria Selector */}
            <div className="lg:col-span-1">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-indigo-600" />
                    <h2 className="text-sm font-semibold text-slate-900">
                      2. Select Criteria
                    </h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllCriteria}
                    disabled={criteria.length === 0}
                  >
                    {selectedCriteriaIds.length === criteria.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </div>

                <div className="max-h-[400px] space-y-2 overflow-y-auto">
                  {criteriaLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-16 animate-pulse rounded-md bg-slate-100"
                        />
                      ))}
                    </div>
                  ) : criteria.length === 0 ? (
                    <div className="flex flex-col items-center py-8 text-slate-400">
                      <ListChecks className="mb-2 h-8 w-8" />
                      <p className="text-sm">No criteria yet</p>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate('/criteria')}
                      >
                        Create criteria
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    criteria.map((criterion: Criterion) => {
                      const isSelected = selectedCriteriaIds.includes(
                        criterion.id
                      );
                      return (
                        <button
                          key={criterion.id}
                          type="button"
                          onClick={() => toggleCriterion(criterion.id)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                            isSelected
                              ? 'border-indigo-200 bg-indigo-50'
                              : 'border-slate-200 hover:bg-slate-50'
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                              isSelected
                                ? 'border-indigo-600 bg-indigo-600'
                                : 'border-slate-300'
                            )}
                          >
                            {isSelected && (
                              <svg
                                className="h-3 w-3 text-white"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <path
                                  d="M2.5 6L5 8.5L9.5 3.5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900">
                              {criterion.name}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Badge variant="outline" className="text-[10px]">
                                {CRITERION_TYPE_LABELS[criterion.type]}
                              </Badge>
                              <span>Weight: {criterion.weight}/5</span>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right: Run Panel */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <FlaskConical className="h-4 w-4 text-indigo-600" />
                <h2 className="text-sm font-semibold text-slate-900">
                  3. Run
                </h2>
              </div>

              {/* Mode toggle */}
              <div className="mb-3 flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => setEvaluationMode('screening')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors',
                    evaluationMode === 'screening'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  )}
                >
                  <ScanSearch className="h-3.5 w-3.5" />
                  Screening
                </button>
                <button
                  type="button"
                  onClick={() => setEvaluationMode('full')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors',
                    evaluationMode === 'full'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  )}
                >
                  <Microscope className="h-3.5 w-3.5" />
                  Full Evaluation
                </button>
              </div>

              <EvalRunner
                sourceIds={selectedSourceIds}
                criteriaIds={selectedCriteriaIds}
                evaluationMode={evaluationMode}
                onComplete={handleEvalComplete}
              />
            </div>
          </div>
        </TabsContent>

        {/* Kanban Tab */}
        <TabsContent value="kanban" className="mt-6">
          <KanbanBoard
            evaluations={evaluations}
            isLoading={evalsLoading}
            onDelete={(id) => deleteEvaluation.mutate(id)}
          />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            {evalsLoading ? (
              <div className="space-y-2 p-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-12 animate-pulse rounded-md bg-slate-100"
                  />
                ))}
              </div>
            ) : evaluations.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-slate-400">
                <History className="mb-2 h-8 w-8" />
                <p className="text-sm">No evaluations yet</p>
                <p className="text-xs">Run your first evaluation above</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Bucket</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evaluations.map((evaluation: Evaluation) => {
                    const bucket =
                      evaluation.score !== null
                        ? getBucketCategory(evaluation.score)
                        : null;
                    return (
                      <TableRow key={evaluation.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {evaluation.sources?.title || evaluation.sources?.file_name || evaluation.source_id.slice(0, 8) + '...'}
                        </TableCell>
                        <TableCell>
                          {evaluation.score !== null ? (
                            <span className="font-mono text-sm font-semibold">
                              {evaluation.score.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {bucket && (
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-xs',
                                bucket === 'include' &&
                                  'border-emerald-200 bg-emerald-50 text-emerald-700',
                                bucket === 'maybe' &&
                                  'border-amber-200 bg-amber-50 text-amber-700',
                                bucket === 'exclude' &&
                                  'border-rose-200 bg-rose-50 text-rose-700'
                              )}
                            >
                              {BUCKET_LABELS[bucket]}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {evaluation.ai_model}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-xs',
                              evaluation.status === 'completed' &&
                                'border-emerald-200 text-emerald-700',
                              evaluation.status === 'processing' &&
                                'border-blue-200 text-blue-700',
                              evaluation.status === 'failed' &&
                                'border-rose-200 text-rose-700',
                              evaluation.status === 'pending' &&
                                'border-slate-200 text-slate-500'
                            )}
                          >
                            {evaluation.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {formatDistanceToNow(new Date(evaluation.created_at), {
                            addSuffix: true,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {evaluation.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  navigate(`/results/${evaluation.id}`)
                                }
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                deleteEvaluation.mutate(evaluation.id)
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
